/**
 * Project Generate page.
 * Two-column layout: template selection + content stats | chat sidebar + project chat.
 */

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProject } from "../hooks/useProjects";
import {
  useProjectContent,
  useProjectTemplates,
  useProjectChat,
  useWorkspaceChat,
  useGenerateProjectDocument,
  useProjectGeneratedDocuments,
  useDeleteProjectGeneratedDocument,
} from "../hooks/useProjectGenerate";
import { useChatSessions } from "../hooks/useChatSessions";
import { useWorkspaceUrl } from "../lib/routes";
import { PageHeader } from "../components/layout/PageHeader";
import BoostGateError from "../components/BoostGateError";
import { InsufficientBoostsError } from "../services/naraDoor";
import { ChatPanel } from "../components/ChatPanel";
import { ChatSidebar } from "../components/ChatSidebar";
import {
  FileText,
  Image,
  Loader2,
  CheckCircle2,
  Sparkles,
  BarChart3,
  Download,
  FileDown,
  Trash2,
  AlertCircle,
  Globe,
} from "lucide-react";

type ChatScope = "project" | "workspace";

export function ProjectGenerate() {
  const { t } = useTranslation();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const urls = useWorkspaceUrl();

  // Data
  const { data: project, isLoading: projectLoading } = useProject(projectId);
  const { data: content, isLoading: contentLoading, isError: contentError } = useProjectContent(projectId);
  const { data: templatesData, isLoading: templatesLoading, isError: templatesError } = useProjectTemplates(projectId);
  const projectChatMutation = useProjectChat(projectId);
  const workspaceChatMutation = useWorkspaceChat();
  const generateMutation = useGenerateProjectDocument();
  const { data: generatedDocs, isError: generatedError, refetch: refetchGenerated } = useProjectGeneratedDocuments(projectId);
  const deleteMutation = useDeleteProjectGeneratedDocument(projectId);

  // State
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [chatScope, setChatScope] = useState<ChatScope>("project");
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [contextSessionIds, setContextSessionIds] = useState<string[]>([]);
  const scopeInitializedRef = useRef<string | null>(null);
  const { data: sessionsData } = useChatSessions({
    projectId: chatScope === "project" ? projectId : undefined,
    scope: chatScope,
  });

  const templates = templatesData?.recipes || [];

  // Auto-select first template
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [templates, selectedTemplateId]);

  // Auto-select most recent session when scope changes or on first load
  useEffect(() => {
    if (scopeInitializedRef.current !== chatScope && sessionsData) {
      scopeInitializedRef.current = chatScope;
      setContextSessionIds([]);
      if (sessionsData.sessions?.length) {
        setActiveSessionId(sessionsData.sessions[0].id);
      } else {
        setActiveSessionId(null);
      }
    }
  }, [chatScope, sessionsData]);

  const handleGenerate = async () => {
    if (!projectId || !selectedTemplateId || generateMutation.isPending) return;
    try {
      await generateMutation.mutateAsync({ projectId, templateId: selectedTemplateId });
      refetchGenerated();
    } catch {
      // error handled by mutation state
    }
  };

  const handleSourceClick = (source: { document_id: string; project_id?: string }) => {
    if (urls && source.project_id) {
      const url = urls.document(source.project_id, source.document_id);
      navigate(url);
    }
  };

  if (projectLoading || contentLoading) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2">
          <div className="h-4 w-16 bg-muted rounded animate-pulse" />
          <div className="h-4 w-4 bg-muted rounded animate-pulse" />
          <div className="h-4 w-24 bg-muted rounded animate-pulse" />
          <div className="h-4 w-4 bg-muted rounded animate-pulse" />
          <div className="h-4 w-36 bg-muted rounded animate-pulse" />
        </div>
        {/* Title skeleton */}
        <div className="h-8 w-56 bg-muted rounded animate-pulse" />
        {/* Template selector skeleton */}
        <div className="h-12 w-full bg-muted rounded-lg animate-pulse" />
        {/* Content cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-medium text-foreground mb-2">
          {t("projectGenerate.projectNotFound")}
        </h2>
      </div>
    );
  }

  const stats = content?.stats;
  const isWorkspaceScope = chatScope === "workspace";

  return (
    <div>
      <PageHeader
        title={t("projectGenerate.title")}
        breadcrumbs={[
          { label: t("nav.projects"), href: urls?.projects },
          { label: project.name, href: urls?.project(projectId!) },
          { label: t("projectGenerate.title") },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN: Template Selection + Content Stats + Generate */}
        <div className="space-y-6">
          {/* Template Selection */}
          <div className="bg-card rounded-lg border border-border p-4">
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              {t("projectGenerate.selectTemplate")}
            </h3>
            {templatesLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : templatesError ? (
              <p className="text-sm text-destructive">{t("projectGenerate.templatesLoadError")}</p>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("projectGenerate.noTemplates")}
              </p>
            ) : (
              <div className="space-y-2">
                {templates.map((tmpl) => (
                  <button
                    key={tmpl.id}
                    onClick={() => setSelectedTemplateId(tmpl.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedTemplateId === tmpl.id
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    <div className="font-medium text-foreground text-sm">{tmpl.name}</div>
                    {tmpl.description && (
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {tmpl.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Content Overview */}
          <div className="bg-card rounded-lg border border-border p-4">
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              {t("projectGenerate.contentOverview")}
            </h3>
            {contentError && <p className="text-sm text-destructive">{t("projectGenerate.contentLoadError")}</p>}
            {stats && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-muted rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-foreground">{stats.total_documents}</div>
                  <div className="text-xs text-muted-foreground">{t("projectGenerate.documents")}</div>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-foreground">{stats.total_word_count.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{t("projectGenerate.words")}</div>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-foreground">{stats.documents_with_text}</div>
                  <div className="text-xs text-muted-foreground">{t("projectGenerate.withText")}</div>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-foreground">{stats.total_images}</div>
                  <div className="text-xs text-muted-foreground">{t("projectGenerate.images")}</div>
                </div>
              </div>
            )}

            {/* Document list */}
            {content?.documents && content.documents.length > 0 && (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {content.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-accent"
                  >
                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-foreground truncate flex-1">{doc.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {doc.word_count.toLocaleString()}w
                    </span>
                    {doc.image_count > 0 && (
                      <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-0.5">
                        <Image className="h-3 w-3" />
                        {doc.image_count}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Workspace shared projects */}
            {content?.shared_projects && content.shared_projects.length > 0 && (
              <div className="mt-4 pt-3 border-t border-border">
                <div className="flex items-center gap-1.5 mb-2">
                  <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t("projectGenerate.sharedProjects", "Workspace Shared")}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {content.shared_projects.map((proj) => (
                    <div
                      key={proj.id}
                      className="flex items-center gap-2 text-sm py-1.5 px-2 rounded bg-muted/50"
                    >
                      <Globe className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                      <span className="text-foreground truncate flex-1">{proj.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {proj.document_count} {t("projectGenerate.docs", "docs")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={!selectedTemplateId || generateMutation.isPending || !stats?.documents_with_text}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("projectGenerate.generating")}
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" />
                {t("projectGenerate.generateDocument")}
              </>
            )}
          </button>

          {generateMutation.error instanceof InsufficientBoostsError && <BoostGateError error={generateMutation.error} />}

          {generateMutation.isError && !(generateMutation.error instanceof InsufficientBoostsError) && (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-300">
                {generateMutation.error?.message?.includes("resource-exhausted")
                  ? t("projectGenerate.errorRateLimit", "Service is temporarily busy. Please wait a moment and try again.")
                  : t("projectGenerate.error")}
              </p>
            </div>
          )}

          {generatedError && <p className="text-sm text-destructive">{t("generatedDocuments.loadError")}</p>}
          {deleteMutation.isError && <p className="text-sm text-destructive">{t("generatedDocuments.deleteError")}</p>}

          {/* Generated Documents */}
          {generatedDocs && generatedDocs.documents.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <FileDown className="h-4 w-4" />
                {t("projectGenerate.generatedDocuments")}
              </h3>
              <div className="space-y-2">
                {generatedDocs.documents.map((doc) => {
                  const isActive = doc.status && !["completed", "failed"].includes(doc.status);
                  const isFailed = doc.status === "failed";
                  const isCompleted = !doc.status || doc.status === "completed";

                  return (
                    <div
                      key={doc.id}
                      className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors ${
                        isFailed
                          ? "border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/30"
                          : isActive
                          ? "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30"
                          : "border-border hover:bg-accent"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm text-foreground truncate">{doc.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                          {doc.created_at && new Date(doc.created_at).toLocaleString()}
                          {isCompleted && doc.file_size_bytes && (
                            <span>({(doc.file_size_bytes / 1024).toFixed(1)} KB)</span>
                          )}
                          {isActive && (
                            <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              {doc.status === "pending" && t("projectGenerate.statusPending", "Starting...")}
                              {doc.status === "generating" && (
                                doc.status_detail
                                  ? doc.status_detail
                                  : t("projectGenerate.statusGenerating", "Generating with AI...")
                              )}
                              {doc.status === "rendering" && t("projectGenerate.statusRendering", "Rendering PDF...")}
                            </span>
                          )}
                          {isFailed && (
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400" title={doc.error_message || ""}>
                              <AlertCircle className="h-3 w-3" />
                              {t("projectGenerate.statusFailed", "Generation failed")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isCompleted && doc.download_url && (
                          <a
                            href={doc.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 rounded-lg transition-colors"
                            title={t("projectGenerate.download")}
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        )}
                        {isCompleted && (
                          <button
                            onClick={() => deleteMutation.mutate({ documentId: doc.id })}
                            disabled={deleteMutation.isPending}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors disabled:opacity-50"
                            title={t("projectGenerate.deleteGenerated")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Chat */}
        <div className="space-y-2">
          {/* Scope Toggle */}
          <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
            <button
              onClick={() => setChatScope("project")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chatScope === "project"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("workspaceChat.scopeProject")}
            </button>
            <button
              onClick={() => setChatScope("workspace")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chatScope === "workspace"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("workspaceChat.scopeWorkspace")}
            </button>
          </div>

          {/* Chat area with sidebar */}
          <div className="flex gap-3">
            <ChatSidebar
              projectId={chatScope === "project" ? projectId : undefined}
              scope={chatScope}
              activeSessionId={activeSessionId}
              onSelectSession={setActiveSessionId}
              onNewSession={() => setActiveSessionId(null)}
              selectedContextIds={contextSessionIds}
              onToggleContext={(id) =>
                setContextSessionIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                )
              }
            />
            <div className="flex-1 min-w-0">
              <ChatPanel
                key={`${chatScope}-${activeSessionId}`}
                mutation={isWorkspaceScope ? workspaceChatMutation : projectChatMutation}
                sessionId={activeSessionId}
                onSessionIdChange={setActiveSessionId}
                contextSessionIds={contextSessionIds}
                onSourceClick={handleSourceClick}
                title={
                  isWorkspaceScope
                    ? t("workspaceChat.title")
                    : t("projectGenerate.projectChat")
                }
                description={
                  isWorkspaceScope
                    ? t("workspaceChat.description")
                    : t("projectGenerate.chatDescription")
                }
                emptyMessage={
                  isWorkspaceScope
                    ? t("workspaceChat.empty")
                    : t("projectGenerate.chatEmpty")
                }
                height="h-[calc(100vh-240px)]"
                showSourceAttribution={isWorkspaceScope}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
